import { Box, Container, Flex, Button } from '@chakra-ui/react';
import { colors } from 'constants/constants';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type { HeroImageProps } from 'types/umbraco';

export default function HeroImage({
  imageOrVideo,
  backgroundColor,
  text,
  textColor,
  button,
  buttonStyle,
  vertikalTextPosition = 'Center',
  horisontellTextPosition = 'Center',
  useSmallHeroHeight = false,
}: HeroImageProps): JSX.Element {
  const router = useRouter();
  const image = Array.isArray(imageOrVideo) && imageOrVideo.length > 0 
    ? imageOrVideo[0] 
    : null;
  const buttonData = Array.isArray(button) && button.length > 0 
    ? button[0] 
    : null;


  const isVideo = image?.mediaType === 'umbracoMediaVideo';

  const getJustifyValue = (position: string) => {
    switch (position) {
      case 'Top':
        return 'flex-start';
      case 'Bottom':
        return 'flex-end';
      case 'Center':
      default:
        return 'center';
    }
  };

  const getHorisontellValue = (position: string) => {
    switch (position) {
      case 'Left':
        return 'flex-start';
      case 'Right':
        return 'flex-end';
      case 'Center':
      default:
        return 'center';
    }
  };

  const getTextAlignValue = (position: string) => {
    switch (position) {
      case 'Left':
        return 'left';
      case 'Right':
        return 'right';
      case 'Center':
      default:
        return 'center';
    }
  };

  const getMarginValue = (position: string) => {
    switch (position) {
      case 'Left':
        return { marginLeft: 0, marginRight: 'auto' };
      case 'Right':
        return { marginLeft: 'auto', marginRight: 0 };
      case 'Center':
      default:
        return { marginLeft: 'auto', marginRight: 'auto' };
    }
  };

  return (
    <Box 
      h={{ base: useSmallHeroHeight ? 300 : 400, md: useSmallHeroHeight ? 400 : 500, lg: useSmallHeroHeight ? 400 : 630 }}
      bg={backgroundColor?.value || '#fff'}
      pos="relative"
    >
      <Container maxW="7xl" h="100%">
        <Flex
          h="100%"
          align={getHorisontellValue(horisontellTextPosition)}
          justify={getJustifyValue(vertikalTextPosition)}
          pos="relative"
          zIndex="11"
          flexDirection="column"
          gap={6}
        >
          {text?.markup && (
            <Box 
              color={textColor?.value || 'white'} 
              textAlign={getTextAlignValue(horisontellTextPosition) as any}
              w={{ base: '70%', sm: '90%', md: '85%', lg: '30%', xl: '30%' }}
              maxW="800px"
              {...getMarginValue(horisontellTextPosition)}
              dangerouslySetInnerHTML={{ __html: text.markup }}
            />
          )}
          {buttonData?.title && (
            <Button
              as="a"
              href={buttonData.route?.path || buttonData.url}
              target={buttonData.target}
              variant={buttonStyle?.toLowerCase() || 'orange'}
              textAlign={getTextAlignValue(horisontellTextPosition) as any}
              {...getMarginValue(horisontellTextPosition)}
            >
              {buttonData.title}
            </Button>
          )}
        </Flex>
      </Container>

      {image?.url && (
        isVideo ? (
          <video
            autoPlay={true}
            loop={true}
            playsInline={true}
            muted={true}
            style={{
              pointerEvents: 'none',
              userSelect: 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          >
            <source src={image.url} />
          </video>
        ) : (
          <Image
            src={image.url}
            fill
            className="heroImageBackgroundFit"
            priority={true}
            loading="eager"
            alt={image.name || 'Hero image'}
          />
        )
      )}

      <Box
        bg={colors.black}
        pos="absolute"
        w="100%"
        h="100%"
        top="0"
        left="0"
        opacity={0.1}
        zIndex="1"
      />
      <style jsx global>{`
        .heroImageBackgroundFit {
          object-fit: cover;
        }
      `}</style>
    </Box>
  );
}
  